import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../config/api';
import { appendAiccUserParams } from '../utils/aiccIdentity';
import { 
  ChevronLeft, 
  ChevronRight, 
  Play, 
  Pause, 
  CheckCircle2,
  CheckCircle,
  AlertCircle,
  Circle,
  Video,
  FileText,
  Package,
  Code,
  Youtube,
  ExternalLink,
  Clock,
  Award,
  Download,
  Maximize,
  Volume2,
  VolumeX,
  SkipForward,
  Menu,
  X,
  ClipboardList,
  Bot,
  Sparkles,
  GraduationCap,
  Globe
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Progress } from '../components/ui/progress';
import JSONContentPlayer from '../components/JSONContentPlayer';
import NaviCoach from '../components/NaviCoach';
import ContentFeedback from '../components/ContentFeedback';
const API = `${API_BASE}/api`;

// Helper to convert storage URLs to proxy URLs
const getProxyUrl = (url, token) => {
  if (!url) return null;
  
  // Check if it's an Emergent storage URL
  if (url.includes('integrations.emergentagent.com/objstore')) {
    // Extract the path after 'navigate-lms/'
    const match = url.match(/navigate-lms\/(.+)$/);
    if (match) {
      return `${API}/files/${match[1]}`;
    }
  }
  
  // Return original URL for external URLs (YouTube, etc.)
  return url;
};

const CONTENT_ICONS = {
  video: Video,
  embed: Youtube,
  pdf: FileText,
  scorm: Package,
  json: Code,
  quiz: ClipboardList,
};

// Helper to check if all non-quiz content in a module is completed
const isModuleContentCompleted = (module) => {
  if (!module?.contents) return true;
  
  const nonQuizContents = module.contents.filter(c => c.content_type !== 'quiz');
  if (nonQuizContents.length === 0) return true;
  
  return nonQuizContents.every(c => c.user_progress?.completed);
};

// AICC Player Component with HACP support
function AICCPlayer({ content, course, courseId, token, user, onComplete, iframeRef, isFullscreen, onToggleFullscreen, canComplete }) {
  const [launchUrl, setLaunchUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  
  // Check multiple sources for the URL: content level, nested content, and course level
  const baseUrl = content?.aicc_url || content?.iframe_url || content?.content?.aicc_url || course?.aicc_url || course?.iframe_url;
  const trackingMode = content?.tracking_mode || content?.content?.tracking_mode || 'iframe_only';
  
  // Use shared utility to append user params to AICC URL
  const appendUserParams = (url) => appendAiccUserParams(url, user);
  
  // Track user interaction with the iframe
  useEffect(() => {
    const handleIframeInteraction = () => {
      setHasInteracted(true);
    };
    
    // Use a timer to detect interaction (user staying on content)
    const interactionTimer = setTimeout(() => {
      setHasInteracted(true);
    }, 30000); // 30 seconds minimum engagement
    
    return () => clearTimeout(interactionTimer);
  }, [content?.content_id]);
  
  useEffect(() => {
    const initSession = async () => {
      if (!baseUrl) {
        setLoading(false);
        return;
      }
      
      // If tracking mode is 'iframe_only', use direct URL without HACP parameters
      // This is common for Intuition and other simple iframe embed providers
      if (trackingMode === 'iframe_only') {
        console.log('AICC: Using direct iframe mode (no HACP)');
        // Append user identification params for seamless login on AICC provider
        const urlWithUser = appendUserParams(baseUrl);
        console.log('AICC: Launch URL with user params:', urlWithUser);
        setLaunchUrl(urlWithUser);
        setLoading(false);
        return;
      }
      
      try {
        // Get HACP launch URL from backend for full AICC tracking
        const response = await axios.post(
          `${API}/aicc/launch?course_id=${courseId}&content_id=${content.content_id}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Append user params to HACP launch URL as well
        setLaunchUrl(appendUserParams(response.data.launch_url));
      } catch (err) {
        console.error('AICC launch error:', err);
        // Fallback to direct URL if HACP setup fails - with user params
        setLaunchUrl(appendUserParams(baseUrl));
      } finally {
        setLoading(false);
      }
    };
    
    initSession();
  }, [baseUrl, courseId, content?.content_id, token, trackingMode, user]);
  
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-zinc-900">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-navigate-gold border-t-transparent mb-4"></div>
        <p className="text-zinc-400">Initializing content...</p>
      </div>
    );
  }
  
  if (!launchUrl) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-zinc-400 p-8">
        <Globe className="h-12 w-12 mb-4" />
        <p className="mb-2">AICC content URL not configured</p>
        <p className="text-xs text-zinc-600">Check Course Builder to ensure the iframe URL is set correctly.</p>
      </div>
    );
  }
  
  return (
    <div className={`w-full h-full bg-black ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
      <iframe
        ref={iframeRef}
        src={launchUrl}
        className="w-full h-full"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation"
        allowFullScreen
        title={content?.title || 'AICC Content'}
        onLoad={() => setHasInteracted(false)}
        onError={(e) => {
          console.error('Iframe load error:', e);
          setError('Failed to load content. The external provider may be blocking iframe embedding.');
        }}
      />
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-zinc-900/90 z-10">
          <div className="text-center p-8 max-w-md">
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <p className="text-white mb-2">Content Load Error</p>
            <p className="text-zinc-400 text-sm mb-4">{error}</p>
            <a
              href={launchUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-navigate-gold text-black rounded-lg font-medium hover:bg-navigate-gold-light transition-colors inline-flex items-center gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              Open in New Tab Instead
            </a>
          </div>
        </div>
      )}
      {/* Exit fullscreen button when in fullscreen mode */}
      {isFullscreen && (
        <button
          onClick={onToggleFullscreen}
          className="fixed top-4 right-4 z-50 px-4 py-2 bg-zinc-800/90 text-white rounded-lg font-medium hover:bg-zinc-700 transition-colors flex items-center gap-2"
        >
          <X className="h-4 w-4" />
          Exit Fullscreen
        </button>
      )}
    </div>
  );
}

export default function CoursePlayer() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeContent, setActiveContent] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [naviCoachOpen, setNaviCoachOpen] = useState(false);
  const [activeModuleForNavi, setActiveModuleForNavi] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [iframeContentReady, setIframeContentReady] = useState(false);
  
  const videoRef = useRef(null);
  const iframeRef = useRef(null);
  const progressUpdateRef = useRef(null);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchCourseData();
    return () => {
      if (progressUpdateRef.current) {
        clearInterval(progressUpdateRef.current);
      }
    };
  }, [courseId, token]);

  // Track iframe content engagement time
  useEffect(() => {
    if (activeContent?.content_type === 'aicc') {
      setIframeContentReady(false);
      // Require 30 seconds of engagement before allowing completion
      const timer = setTimeout(() => {
        setIframeContentReady(true);
      }, 30000);
      return () => clearTimeout(timer);
    }
  }, [activeContent?.content_id, activeContent?.content_type]);

  const fetchCourseData = async () => {
    try {
      const res = await axios.get(`${API}/courses/${courseId}/player`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourse(res.data.course);
      setEnrollment(res.data.enrollment);
      setProgress(res.data.progress);
      
      // Set first incomplete content as active
      const modules = res.data.course.modules || [];
      for (const module of modules) {
        for (const content of module.contents || []) {
          if (!content.user_progress?.completed) {
            setActiveContent({ ...content, module_id: module.module_id });
            break;
          }
        }
        if (activeContent) break;
      }
      
      // Default to first content if all complete
      if (!activeContent && modules[0]?.contents?.[0]) {
        const firstContent = modules[0].contents[0];
        setActiveContent({ ...firstContent, module_id: modules[0].module_id });
      }
    } catch (error) {
      console.error('Error loading course:', error);
      if (error.response?.status === 402) {
        toast.error('Please enroll in this course to access content');
        navigate(`/courses/${courseId}`);
      } else {
        toast.error('Failed to load course');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateProgress = useCallback(async (contentId, progressPercent, completed = false) => {
    if (!token) return;
    
    try {
      await axios.post(`${API}/courses/${courseId}/progress`, {
        content_id: contentId,
        progress_percent: progressPercent,
        completed,
        time_spent_seconds: 0,
        last_position: progressPercent
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (completed) {
        fetchCourseData(); // Refresh to get updated progress
        toast.success('Content completed!');
      }
    } catch (error) {
      console.error('Failed to update progress:', error);
    }
  }, [courseId, token]);

  const markAsComplete = async () => {
    if (!activeContent) return;
    await updateProgress(activeContent.content_id, 100, true);
  };

  const navigateToContent = (direction) => {
    if (!course) return;
    
    const allContents = [];
    course.modules.forEach(module => {
      (module.contents || []).forEach(content => {
        allContents.push({ ...content, module_id: module.module_id });
      });
    });
    
    const currentIndex = allContents.findIndex(c => c.content_id === activeContent?.content_id);
    const newIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
    
    if (newIndex >= 0 && newIndex < allContents.length) {
      setActiveContent(allContents[newIndex]);
    }
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const videoIdMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?\s]+)/);
    return videoIdMatch ? `https://www.youtube.com/embed/${videoIdMatch[1]}?autoplay=1` : url;
  };

  const getVimeoEmbedUrl = (url) => {
    if (!url) return null;
    const videoIdMatch = url.match(/vimeo\.com\/(\d+)/);
    return videoIdMatch ? `https://player.vimeo.com/video/${videoIdMatch[1]}?autoplay=1` : url;
  };

  const renderContentPlayer = () => {
    if (!activeContent) {
      return (
        <div className="flex items-center justify-center h-full text-zinc-500">
          <p>Select content to begin</p>
        </div>
      );
    }

    const { content_type, file_url, embed_url, embed_code, title, content } = activeContent;
    
    // Convert storage URLs to proxy URLs
    const proxyFileUrl = getProxyUrl(file_url, token);
    
    // Helper to extract URL from embed_code (e.g., iframe src)
    const extractUrlFromEmbedCode = (code) => {
      if (!code) return null;
      // Try to extract src from iframe
      const srcMatch = code.match(/src=["']([^"']+)["']/i);
      return srcMatch ? srcMatch[1] : null;
    };

    switch (content_type) {
      case 'aicc':
        // AICC content - render using dedicated AICC Player component
        // For iframe courses, users can mark complete after 30 seconds of engagement
        return (
          <AICCPlayer
            content={activeContent}
            course={course}
            courseId={courseId}
            token={token}
            user={user}
            onComplete={markAsComplete}
            iframeRef={iframeRef}
            isFullscreen={isFullscreen}
            onToggleFullscreen={() => setIsFullscreen(!isFullscreen)}
            canComplete={iframeContentReady || activeContent.user_progress?.completed}
          />
        );

      case 'embed':
        // Check if this is part of an AICC course (course.content_type === 'aicc' or has aicc_url)
        if (course?.content_type === 'aicc' || activeContent?.aicc_url || course?.aicc_url) {
          return (
            <AICCPlayer
              content={activeContent}
              course={course}
              courseId={courseId}
              token={token}
              user={user}
              onComplete={markAsComplete}
              iframeRef={iframeRef}
              isFullscreen={isFullscreen}
              onToggleFullscreen={() => setIsFullscreen(!isFullscreen)}
              canComplete={iframeContentReady || activeContent.user_progress?.completed}
            />
          );
        }
        
        // Regular embed handling below
        {
          // Get embed URL from direct field or extract from embed_code
          const embedCodeContent = embed_code || content?.embed_code;
          let embedSrc = embed_url || extractUrlFromEmbedCode(embedCodeContent);
          
          // Check if this is an external LMS URL that blocks iframe embedding
          const isExternalLMS = embedSrc?.includes('moodle') || 
                               embedSrc?.includes('/mod/') || 
                               embedSrc?.includes('/course/view.php') ||
                               embedSrc?.includes('hstgr.cloud') ||
                               embedSrc?.includes('articulate') ||
                               embedSrc?.includes('scorm') ||
                               embedSrc?.includes('ispring');
          
          if (isExternalLMS && embedSrc) {
            // External LMS - show launch button instead of iframe
            return (
              <div className="relative w-full h-full flex flex-col bg-zinc-900">
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center p-8 max-w-lg">
                    <div className="w-24 h-24 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-cyan-500/30">
                      <ExternalLink className="h-12 w-12 text-cyan-400" />
                    </div>
                    <h2 className="text-2xl font-semibold text-white mb-3">{title || 'External Content'}</h2>
                    <p className="text-zinc-400 mb-8">
                      This content is hosted on an external platform. Click below to open it in a new tab.
                    </p>
                    <div className="space-y-4">
                      <a
                        href={embedSrc}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="launch-external-btn"
                        className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-medium hover:from-cyan-600 hover:to-blue-600 transition-all w-full justify-center shadow-lg shadow-cyan-500/25"
                      >
                        <ExternalLink className="h-5 w-5" />
                        Launch Content
                      </a>
                      <button
                        onClick={markAsComplete}
                        disabled={!iframeContentReady}
                        className={`px-6 py-3 rounded-xl font-medium transition-colors flex items-center gap-2 w-full justify-center ${
                          iframeContentReady 
                            ? 'bg-navigate-gold text-black hover:bg-navigate-gold-light'
                            : 'bg-zinc-600 text-zinc-400 cursor-not-allowed'
                        }`}
                      >
                        <CheckCircle2 className="h-5 w-5" />
                        Mark as Complete
                      </button>
                    </div>
                    <p className="text-xs text-zinc-500 mt-6">
                      Note: External platforms often don&apos;t support iframe embedding for security reasons.
                    </p>
                  </div>
                </div>
              </div>
            );
          }
          
          if (embedSrc?.includes('youtube')) {
            embedSrc = getYouTubeEmbedUrl(embedSrc);
          } else if (embedSrc?.includes('vimeo')) {
            embedSrc = getVimeoEmbedUrl(embedSrc);
          }
          return (
            <div className="relative w-full h-full bg-black">
              <iframe
                ref={iframeRef}
                src={embedSrc}
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={title}
              />
              <div className="absolute bottom-4 right-4">
                <button
                  onClick={markAsComplete}
                  disabled={!iframeContentReady}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                    iframeContentReady 
                      ? 'bg-navigate-gold text-black hover:bg-navigate-gold-light'
                      : 'bg-zinc-600 text-zinc-400 cursor-not-allowed'
                  }`}
                >
                  <CheckCircle2 className="h-5 w-5" />
                  Mark Complete
                </button>
              </div>
            </div>
          );
        }
        
      case 'video':
        return (
          <div className="relative w-full h-full bg-black">
            <video
              ref={videoRef}
              src={proxyFileUrl}
              className="w-full h-full"
              controls
              autoPlay
              onEnded={() => markAsComplete()}
              onTimeUpdate={(e) => {
                const percent = (e.target.currentTime / e.target.duration) * 100;
                if (percent > 90) {
                  markAsComplete();
                }
              }}
            />
          </div>
        );

      case 'external':
      case 'moodle':
        // Generic external content - launches in new tab with completion callback support
        const externalUrl = activeContent.external_url || activeContent.moodle_url || activeContent.embed_url;
        const providerName = activeContent.provider_name || 'External Platform';
        const allowIframe = activeContent.allow_iframe === true;
        
        // If iframe is allowed and URL exists, try to embed
        if (allowIframe && externalUrl) {
          return (
            <div className="relative w-full h-full bg-black">
              <iframe
                ref={iframeRef}
                src={externalUrl}
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
                title={title}
                onError={() => console.log('Iframe load error - external content may block embedding')}
              />
              <div className="absolute bottom-4 right-4 flex gap-2">
                <a
                  href={externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-zinc-700 text-white rounded-lg font-medium hover:bg-zinc-600 transition-colors flex items-center gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  Open in New Tab
                </a>
                <button
                  onClick={markAsComplete}
                  className="px-4 py-2 bg-navigate-gold text-black rounded-lg font-medium hover:bg-navigate-gold-light transition-colors flex items-center gap-2"
                >
                  <CheckCircle2 className="h-5 w-5" />
                  Mark Complete
                </button>
              </div>
            </div>
          );
        }
        
        // Default: Show launch button (most external LMS platforms block iframe embedding)
        return (
          <div className="relative w-full h-full flex flex-col bg-zinc-900">
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center p-8 max-w-lg">
                <div className="w-24 h-24 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-cyan-500/30">
                  <ExternalLink className="h-12 w-12 text-cyan-400" />
                </div>
                <h2 className="text-2xl font-semibold text-white mb-2">{title || 'External Content'}</h2>
                <p className="text-sm text-cyan-400 mb-4">{providerName}</p>
                <p className="text-zinc-400 mb-8">
                  {activeContent.description || `This content is hosted on ${providerName}. Click below to launch it in a new tab and complete the training.`}
                </p>
                <div className="space-y-4">
                  <a
                    href={externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="launch-external-btn"
                    className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-medium hover:from-cyan-600 hover:to-blue-600 transition-all w-full justify-center shadow-lg shadow-cyan-500/25"
                  >
                    <ExternalLink className="h-5 w-5" />
                    Launch External Content
                  </a>
                  <button
                    onClick={markAsComplete}
                    data-testid="mark-complete-btn"
                    className="px-6 py-3 bg-navigate-gold text-black rounded-xl font-medium hover:bg-navigate-gold-light transition-colors flex items-center gap-2 w-full justify-center"
                  >
                    <CheckCircle2 className="h-5 w-5" />
                    Mark as Complete
                  </button>
                </div>
                <p className="text-xs text-zinc-500 mt-6">
                  Complete the content on {providerName}, then return here and click &quot;Mark as Complete&quot; to track your progress.
                </p>
              </div>
            </div>
          </div>
        );

      case 'pdf':
        // For PDFs, we need to either use a direct download or an embedded viewer
        // Google Docs viewer doesn't work with authenticated URLs, so let's use an iframe with auth
        return (
          <div className="relative w-full h-full flex flex-col">
            <div className="flex-1 bg-zinc-900 flex items-center justify-center">
              <div className="text-center p-8">
                <FileText className="h-16 w-16 text-orange-400 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-white mb-2">{title}</h2>
                <p className="text-zinc-400 mb-6">PDF Document</p>
                <a
                  href={proxyFileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-navigate-gold text-black rounded-lg font-medium hover:bg-navigate-gold-light transition-colors"
                >
                  <ExternalLink className="h-5 w-5" />
                  Open PDF in New Tab
                </a>
              </div>
            </div>
            <div className="p-4 bg-navigate-surface border-t border-zinc-800 flex justify-between items-center">
              <a
                href={proxyFileUrl}
                download
                className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
              >
                <Download className="h-5 w-5" />
                Download PDF
              </a>
              <button
                onClick={markAsComplete}
                className="px-4 py-2 bg-navigate-gold text-black rounded-lg font-medium hover:bg-navigate-gold-light transition-colors flex items-center gap-2"
              >
                <CheckCircle2 className="h-5 w-5" />
                Mark Complete
              </button>
            </div>
          </div>
        );

      case 'scorm':
        // For SCORM packages with package_id or scorm_package_id, link to dedicated player
        const scormPackageId = activeContent.scorm_package_id || activeContent.package_id || 
          (activeContent.content?.package_id);
        
        if (scormPackageId) {
          return (
            <div className="relative w-full h-full flex flex-col items-center justify-center bg-zinc-900 text-center p-8">
              <Package className="h-16 w-16 text-[#1EBFB3] mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">{title}</h2>
              <p className="text-zinc-400 mb-6 max-w-md">
                Interactive SCORM {activeContent.scorm_version || activeContent.content?.scorm_version || '1.2'} content with full tracking support.
              </p>
              
              <a
                href={`/scorm/${scormPackageId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 bg-navigate-gold text-black rounded-lg font-medium hover:bg-navigate-gold-light transition-colors flex items-center gap-2"
                data-testid="launch-scorm-btn"
              >
                <Play className="h-5 w-5" />
                Launch SCORM Player
              </a>
              
              <button
                onClick={markAsComplete}
                className="mt-4 text-sm text-zinc-400 hover:text-white transition-colors"
              >
                Or mark as complete manually
              </button>
            </div>
          );
        }
        
        // SCORM content without package_id - show configuration error
        return (
          <div className="relative w-full h-full flex flex-col items-center justify-center bg-zinc-900 text-center p-8">
            <AlertCircle className="h-16 w-16 text-amber-400 mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">{title}</h2>
            <p className="text-amber-400 mb-2">SCORM Package Not Configured</p>
            <p className="text-zinc-400 mb-6 max-w-md text-sm">
              This SCORM module has not been properly configured. The course creator needs to upload a SCORM package 
              and save the course in Course Builder.
            </p>
            
            <button
              onClick={markAsComplete}
              className="px-6 py-3 bg-zinc-700 text-white rounded-lg font-medium hover:bg-zinc-600 transition-colors flex items-center gap-2"
            >
              <CheckCircle className="h-5 w-5" />
              Mark as Complete
            </button>
          </div>
        );

      case 'json':
        return (
          <JSONContentPlayer
            content={activeContent}
            fileUrl={proxyFileUrl}
            token={token}
            onComplete={markAsComplete}
            isCompleted={activeContent.user_progress?.completed}
          />
        );

      case 'quiz':
        // For quiz content, link to the quiz player
        return (
          <div className="relative w-full h-full flex flex-col items-center justify-center bg-zinc-900 text-center p-8">
            <ClipboardList className="h-16 w-16 text-navigate-gold mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">{title}</h2>
            <p className="text-zinc-400 mb-6 max-w-md">
              Test your knowledge with this assessment. Your progress will be tracked automatically.
            </p>
            
            <a
              href={`/quiz/${activeContent.quiz_id}`}
              className="px-6 py-3 bg-navigate-gold text-black rounded-lg font-medium hover:bg-navigate-gold-light transition-colors flex items-center gap-2"
              data-testid="start-quiz-btn"
            >
              <Play className="h-5 w-5" />
              Start Quiz
            </a>
            
            {activeContent.user_progress?.completed && (
              <div className="mt-4 flex items-center gap-2 text-green-400">
                <CheckCircle2 className="h-5 w-5" />
                <span>Quiz completed</span>
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="flex items-center justify-center h-full text-zinc-500">
            <p>Unsupported content type</p>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-navigate-dark flex items-center justify-center">
        <div className="text-white">Loading course...</div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-navigate-dark flex items-center justify-center">
        <div className="text-center">
          <p className="text-white mb-4">Course not found</p>
          <Link to="/courses" className="text-navigate-gold hover:underline">
            Back to Courses
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navigate-dark flex flex-col" data-testid="course-player">
      {/* Header */}
      <header className="h-16 bg-navigate-surface border-b border-zinc-800 flex items-center px-4 gap-4">
        <button
          onClick={() => navigate(`/courses/${courseId}`)}
          className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
        >
          <ChevronLeft className="h-5 w-5" />
          <span className="hidden sm:inline">Back to Course</span>
        </button>
        
        <div className="flex-1 min-w-0">
          <h1 className="text-white font-medium truncate">{course.title}</h1>
        </div>

        <div className="flex items-center gap-4">
          {/* Feedback Component */}
          <ContentFeedback
            contentType="course"
            contentId={courseId}
            token={token}
            size="sm"
            className="hidden sm:flex"
          />
          
          <div className="hidden sm:flex items-center gap-2 text-sm">
            <span className="text-zinc-400">Progress:</span>
            <span className="text-navigate-gold font-medium">{progress?.overall_percent || 0}%</span>
          </div>
          <Progress value={progress?.overall_percent || 0} className="w-24 h-2 hidden sm:block" />
          
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 text-zinc-400 hover:text-white lg:hidden"
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Main Content Area */}
        <main className={`flex-1 flex flex-col ${sidebarOpen ? 'lg:mr-80' : ''}`}>
          {/* Content Player */}
          <div className="flex-1 bg-black">
            {renderContentPlayer()}
          </div>

          {/* Content Navigation */}
          <div className="h-16 bg-navigate-surface border-t border-zinc-800 flex items-center justify-between px-4">
            <button
              onClick={() => navigateToContent('prev')}
              className="flex items-center gap-2 px-4 py-2 text-zinc-400 hover:text-white transition-colors"
            >
              <ChevronLeft className="h-5 w-5" />
              <span className="hidden sm:inline">Previous</span>
            </button>

            {/* Center section: Title + AICC controls */}
            <div className="flex items-center gap-4">
              <p className="text-sm text-zinc-400 truncate max-w-xs hidden lg:block">
                {activeContent?.title}
              </p>
              
              {/* AICC/Iframe specific controls */}
              {(activeContent?.content_type === 'aicc' || activeContent?.content_type === 'embed' || course?.content_type === 'aicc' || activeContent?.aicc_url || activeContent?.iframe_url) && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setIsFullscreen(!isFullscreen)}
                    className="px-3 py-1.5 bg-zinc-700 text-white text-sm rounded-lg font-medium hover:bg-zinc-600 transition-colors flex items-center gap-1"
                    title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                    data-testid="fullscreen-btn"
                  >
                    <Maximize className="h-4 w-4" />
                    <span className="hidden sm:inline">{isFullscreen ? 'Exit' : 'Fullscreen'}</span>
                  </button>
                  <a
                    href={activeContent?.aicc_url || activeContent?.iframe_url || activeContent?.content?.aicc_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1.5 bg-zinc-700 text-white text-sm rounded-lg font-medium hover:bg-zinc-600 transition-colors flex items-center gap-1"
                    data-testid="open-new-tab-btn"
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span className="hidden sm:inline">New Tab</span>
                  </a>
                  <button
                    onClick={markAsComplete}
                    disabled={!iframeContentReady && !activeContent?.user_progress?.completed}
                    className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors flex items-center gap-1 ${
                      (iframeContentReady || activeContent?.user_progress?.completed)
                        ? 'bg-navigate-gold text-black hover:bg-navigate-gold-light' 
                        : 'bg-zinc-600 text-zinc-400 cursor-not-allowed'
                    }`}
                    title={(iframeContentReady || activeContent?.user_progress?.completed) ? 'Mark complete' : 'Complete content first (30s)'}
                    data-testid="mark-complete-btn"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    <span className="hidden sm:inline">Mark Complete</span>
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={() => navigateToContent('next')}
              className="flex items-center gap-2 px-4 py-2 text-zinc-400 hover:text-white transition-colors"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </main>

        {/* Sidebar - Course Contents */}
        <aside className={`
          fixed lg:static inset-y-0 right-0 w-80 bg-navigate-surface border-l border-zinc-800 
          transform transition-transform duration-300 z-40 mt-16 lg:mt-0
          ${sidebarOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0 lg:hidden'}
        `}>
          <div className="h-full flex flex-col overflow-hidden">
            {/* Sidebar Header */}
            <div className="p-4 border-b border-zinc-800">
              <h2 className="font-heading font-semibold text-white">Course Contents</h2>
              <p className="text-sm text-zinc-400 mt-1">
                {progress?.completed_contents || 0} of {progress?.total_contents || 0} completed
              </p>
            </div>

            {/* Module List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {course.modules?.sort((a, b) => a.order - b.order).map((module, idx) => (
                <div key={module.module_id} className="space-y-2">
                  <h3 className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-navigate-gold/20 text-navigate-gold text-xs flex items-center justify-center">
                      {idx + 1}
                    </span>
                    {module.title}
                  </h3>
                  
                  <div className="pl-8 space-y-1">
                    {(module.contents || []).sort((a, b) => a.order - b.order).map((content) => {
                      const Icon = CONTENT_ICONS[content.content_type] || FileText;
                      const isActive = activeContent?.content_id === content.content_id;
                      const isCompleted = content.user_progress?.completed;
                      const isQuiz = content.content_type === 'quiz';
                      
                      // Check if quiz/assessment should be locked (module content not completed)
                      const moduleContentDone = isModuleContentCompleted(module);
                      const isLocked = isQuiz && !moduleContentDone;
                      
                      return (
                        <button
                          key={content.content_id}
                          onClick={() => {
                            if (isLocked) {
                              toast.error('Complete all module lessons first', {
                                description: 'You must complete all lessons in this module before attempting the assessment.'
                              });
                              return;
                            }
                            setActiveContent({ ...content, module_id: module.module_id });
                          }}
                          disabled={isLocked}
                          className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors ${
                            isLocked
                              ? 'opacity-50 cursor-not-allowed bg-zinc-800/30'
                              : isActive 
                                ? 'bg-navigate-gold/10 text-navigate-gold' 
                                : 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
                          }`}
                          title={isLocked ? 'Complete all module lessons first' : content.title}
                        >
                          <div className="relative">
                            {isCompleted ? (
                              <CheckCircle2 className="h-4 w-4 text-green-400" />
                            ) : isLocked ? (
                              <div className="relative">
                                <Icon className="h-4 w-4 text-zinc-600" />
                                <div className="absolute -top-1 -right-1 w-2 h-2 bg-amber-500 rounded-full" />
                              </div>
                            ) : (
                              <Icon className="h-4 w-4" />
                            )}
                          </div>
                          <span className="text-sm truncate flex-1">
                            {isQuiz ? (content.title.replace('NAVI ', '').replace('Assessment', 'Assessment')) : content.title}
                          </span>
                          {content.duration_minutes > 0 && (
                            <span className="text-xs text-zinc-500">
                              {content.duration_minutes}m
                            </span>
                          )}
                          {isLocked && (
                            <span className="text-xs text-amber-500">Locked</span>
                          )}
                        </button>
                      );
                    })}
                    
                    {/* Ask NAVI Coach Button - Hide for AICC/iframe courses */}
                    {course?.content_type !== 'aicc' && !course?.aicc_url && !course?.iframe_url && (
                      <button
                        onClick={() => {
                          setActiveModuleForNavi({ id: module.module_id, title: module.title });
                          setNaviCoachOpen(true);
                        }}
                        className="w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors bg-gradient-to-r from-[#1EBFB3]/10 to-navigate-gold/10 hover:from-[#1EBFB3]/20 hover:to-navigate-gold/20 border border-[#1EBFB3]/20 mt-2"
                        data-testid={`ask-navi-${module.module_id}`}
                      >
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#1EBFB3] to-navigate-gold flex items-center justify-center">
                          <Bot className="h-3 w-3 text-white" />
                        </div>
                        <span className="text-sm text-[#1EBFB3] flex items-center gap-1">
                          Ask NAVI
                          <Sparkles className="h-3 w-3 text-navigate-gold" />
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Course Completion */}
            {progress?.overall_percent >= 100 && (
              <div className="p-4 border-t border-zinc-800 bg-green-500/10">
                <div className="flex items-center gap-3 text-green-400">
                  <Award className="h-6 w-6" />
                  <div>
                    <p className="font-medium">Course Completed!</p>
                    <Link to="/certificates" className="text-sm hover:underline">
                      View Certificate
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>
      
      {/* NAVI AI Coach Modal */}
      <NaviCoach
        isOpen={naviCoachOpen}
        onClose={() => {
          setNaviCoachOpen(false);
          setActiveModuleForNavi(null);
        }}
        courseId={courseId}
        moduleId={activeModuleForNavi?.id}
        moduleName={activeModuleForNavi?.title}
        token={token}
        onComplete={(assessment) => {
          toast.success('NAVI coaching session complete!', {
            description: 'Your assessment has been saved for instructor review.'
          });
        }}
      />
    </div>
  );
}

// SCORM Player Component
function SCORMPlayer({ content, courseId, token, onComplete }) {
  const iframeRef = useRef(null);
  const [initialized, setInitialized] = useState(false);
  const [scormData, setScormData] = useState(null);

  useEffect(() => {
    initializeSCORM();
    
    // Setup SCORM API bridge
    window.API = {
      LMSInitialize: () => { setInitialized(true); return "true"; },
      LMSFinish: () => { finishSCORM(); return "true"; },
      LMSGetValue: (element) => getScormValue(element),
      LMSSetValue: (element, value) => { setScormValue(element, value); return "true"; },
      LMSCommit: () => { commitScormData(); return "true"; },
      LMSGetLastError: () => "0",
      LMSGetErrorString: () => "",
      LMSGetDiagnostic: () => "",
    };
    
    // SCORM 2004 API
    window.API_1484_11 = window.API;

    return () => {
      delete window.API;
      delete window.API_1484_11;
    };
  }, [content.content_id]);

  const initializeSCORM = async () => {
    try {
      const res = await axios.post(
        `${API}/scorm/initialize?course_id=${courseId}&content_id=${content.content_id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setScormData(res.data.cmi);
      setInitialized(true);
    } catch (error) {
      console.error('SCORM init failed:', error);
    }
  };

  const getScormValue = (element) => {
    if (!scormData) return "";
    const parts = element.replace("cmi.", "").split(".");
    let value = scormData;
    for (const part of parts) {
      value = value?.[part] || "";
    }
    return String(value);
  };

  const setScormValue = (element, value) => {
    if (!scormData) return;
    setScormData(prev => {
      const newData = { ...prev };
      const parts = element.replace("cmi.", "").split(".");
      let current = newData;
      for (let i = 0; i < parts.length - 1; i++) {
        current[parts[i]] = current[parts[i]] || {};
        current = current[parts[i]];
      }
      current[parts[parts.length - 1]] = value;
      return newData;
    });
  };

  const commitScormData = async () => {
    try {
      await axios.post(`${API}/scorm/commit`, {
        content_id: content.content_id,
        cmi_data: scormData
      }, { headers: { Authorization: `Bearer ${token}` }});
      
      // Check if completed
      if (scormData?.core?.lesson_status === 'completed' || scormData?.core?.lesson_status === 'passed') {
        onComplete();
      }
    } catch (error) {
      console.error('SCORM commit failed:', error);
    }
  };

  const finishSCORM = async () => {
    try {
      await axios.post(
        `${API}/scorm/finish?content_id=${content.content_id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
    } catch (error) {
      console.error('SCORM finish failed:', error);
    }
  };

  // For SCORM packages, we'd need to unzip and serve the content
  // For now, show a placeholder with manual complete option
  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center bg-zinc-900 text-center p-8">
      <Package className="h-16 w-16 text-[#1EBFB3] mb-4" />
      <h2 className="text-xl font-semibold text-white mb-2">{content.title}</h2>
      <p className="text-zinc-400 mb-6 max-w-md">
        SCORM Package Loaded. SCORM tracking is enabled for this content.
      </p>
      
      {content.file_url && (
        <a
          href={content.file_url}
          target="_blank"
          rel="noopener noreferrer"
          className="mb-4 flex items-center gap-2 text-navigate-gold hover:underline"
        >
          <Download className="h-4 w-4" />
          Download SCORM Package
        </a>
      )}
      
      <button
        onClick={onComplete}
        className="px-6 py-3 bg-navigate-gold text-black rounded-lg font-medium hover:bg-navigate-gold-light transition-colors flex items-center gap-2"
      >
        <CheckCircle2 className="h-5 w-5" />
        Mark as Complete
      </button>
      
      {initialized && (
        <p className="mt-4 text-xs text-green-400">SCORM API Initialized</p>
      )}
    </div>
  );
}
